// Phase 5 — Transparency UI.
// Small attribution chip rendered beneath every numeric MessagePart whose
// tool emitted a structured Provenance record. Click to open the Show Data
// drawer (next component) for the full audit chain.
//
// Visual: pill with a small "USDA" / "DV" / "CME" / etc. source badge,
// the canonical report name, the as-of-date, and the unit. Hover/focus
// shows the fetched-at timestamp + revision status. The chip itself is
// the trust signal — every number on the screen has one.

import { ExternalLink, FileText } from "lucide-react";
import type { Provenance } from "@/types/consultantProvenance";
import { useProvenanceDrawer } from "./ProvenanceDrawer";

const SOURCE_BADGE_COLOR: Record<string, string> = {
  "USDA-LMR": "bg-amber-700/15 text-amber-800 border-amber-700/30",
  "USDA-NASS": "bg-amber-700/15 text-amber-800 border-amber-700/30",
  "USDA-AMS": "bg-amber-700/15 text-amber-800 border-amber-700/30",
  "USDA-FSIS": "bg-amber-700/15 text-amber-800 border-amber-700/30",
  "USDA-WASDE": "bg-amber-700/15 text-amber-800 border-amber-700/30",
  "DVAuction": "bg-emerald-700/15 text-emerald-800 border-emerald-700/30",
  "CME": "bg-blue-700/15 text-blue-800 border-blue-700/30",
  "EIA": "bg-slate-700/15 text-slate-800 border-slate-700/30",
  "internal-derived": "bg-purple-700/15 text-purple-800 border-purple-700/30",
};

const SOURCE_SHORT: Record<string, string> = {
  "USDA-LMR": "USDA · LMR",
  "USDA-NASS": "USDA · NASS",
  "USDA-AMS": "USDA · AMS",
  "USDA-FSIS": "USDA · FSIS",
  "USDA-WASDE": "USDA · WASDE",
  "DVAuction": "DV Auction",
  "CME": "CME",
  "EIA": "EIA",
  "internal-derived": "Derived",
};

function formatAsOfDate(iso: string): string {
  // YYYY-MM-DD → "Apr 28, 2026" for human eyes
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function RevisionBadge({ provenance }: { provenance: Provenance }) {
  const { revision_status: status, revision_count, original_value } = provenance;

  // Final values from non-revising sources (DVAuction, CME, EIA) get no badge.
  if (status === "n/a") return null;

  // Final values that have NEVER been revised (revision_count = 0 or
  // undefined) — no badge needed; the value is settled.
  if (status === "final" && (revision_count == null || revision_count === 0)) {
    return null;
  }

  const isRevised = status === "revised" || (revision_count != null && revision_count >= 1);
  const isPreliminary = status === "preliminary" && !isRevised;

  // "Final" with revision_count >= 1 means the value was revised at some
  // point but the period is now closed (past the 35/45-day window or annual
  // lock). Show the revision count to communicate "this isn't the original
  // print, it was revised N times before being finalized."
  const cls = isPreliminary
    ? "bg-orange-100 text-orange-900 border-orange-300"
    : isRevised
    ? "bg-blue-100 text-blue-900 border-blue-300"
    : "bg-slate-100 text-slate-900 border-slate-300";

  let label: string;
  if (isPreliminary) {
    label = "PRELIMINARY";
  } else if (isRevised) {
    // Use 🔁 to communicate "value changed" at-a-glance per Phase 4 design.
    const countSuffix = revision_count != null && revision_count > 0 ? ` ×${revision_count}` : "";
    label = `🔁 REVISED${countSuffix}`;
  } else {
    // status === "final" with revision_count >= 1 (caught above; this branch
    // is for completeness only).
    const countSuffix = revision_count != null && revision_count > 0 ? ` ×${revision_count}` : "";
    label = `🔁 FINAL${countSuffix}`;
  }

  // Tooltip carries the raw delta when we have it, e.g.
  // "Originally reported 2,266,000 head; now 2,286,000 (+20,000)."
  let tooltip = `Revision status: ${status}`;
  if (revision_count != null) tooltip += `\nRevision count: ${revision_count}`;
  if (original_value != null) tooltip += `\nOriginal value: ${original_value.toLocaleString()}`;

  return (
    <span
      className={`inline-flex items-center rounded border px-1 py-0 text-[9px] font-semibold tracking-wide ${cls}`}
      title={tooltip}
    >
      {label}
    </span>
  );
}

export function ProvenanceChip({
  provenance,
  surface = "light",
  onClick,
}: {
  provenance: Provenance;
  surface?: "dark" | "light";
  onClick?: () => void;
}) {
  const badgeColor = SOURCE_BADGE_COLOR[provenance.source] ?? SOURCE_BADGE_COLOR["internal-derived"];
  const sourceLabel = SOURCE_SHORT[provenance.source] ?? provenance.source;
  const isLight = surface === "light";

  const chipBg = isLight
    ? "bg-black/5 hover:bg-black/10 border border-black/10 text-[#1A1A1A]"
    : "bg-white/5 hover:bg-white/10 border border-white/10 text-foreground";

  const labelText = [
    provenance.report_name,
    formatAsOfDate(provenance.as_of_date),
    `unit: ${provenance.unit}`,
  ].join(" · ");

  const interactive = typeof onClick === "function";
  const Element = interactive ? "button" : "div";

  return (
    <Element
      type={interactive ? "button" : undefined}
      onClick={onClick}
      title={`Source: ${provenance.source}\nReport: ${provenance.report_name}\nAs of: ${provenance.as_of_date}\nFetched: ${new Date(provenance.fetched_at).toLocaleString()}\nUnit: ${provenance.unit}\nRevision: ${provenance.revision_status}${provenance.usda_url ? `\n\n${provenance.usda_url}` : ""}`}
      className={`group inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] leading-tight transition-colors ${chipBg} ${interactive ? "cursor-pointer" : ""}`}
    >
      <span className={`inline-flex items-center rounded border px-1 py-0 text-[9px] font-semibold tracking-wide ${badgeColor}`}>
        {sourceLabel}
      </span>
      <span className="font-medium opacity-90">{labelText}</span>
      <RevisionBadge provenance={provenance} />
      {provenance.usda_url
        ? <ExternalLink className="h-2.5 w-2.5 opacity-50 group-hover:opacity-100" />
        : interactive
          ? <FileText className="h-2.5 w-2.5 opacity-50 group-hover:opacity-100" />
          : null}
    </Element>
  );
}

/**
 * Render a row of provenance chips. Provenance can be a single record
 * or an array (for derived values that combine multiple sources).
 * Empty / undefined → renders nothing.
 *
 * If a `messageId` is provided, clicking a chip opens the Show Data
 * drawer with the full audit chain for that message (Phase 5 part 2).
 * Without `messageId`, chips are display-only.
 */
export function ProvenanceRow({
  provenance,
  surface = "light",
  messageId,
}: {
  provenance: Provenance | Provenance[] | undefined;
  surface?: "dark" | "light";
  messageId?: string | null;
}) {
  const { openDrawer } = useProvenanceDrawer();
  if (!provenance) return null;
  const list = Array.isArray(provenance) ? provenance : [provenance];
  if (list.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {list.map((p, i) => (
        <ProvenanceChip
          key={`${p.report_slug}-${p.as_of_date}-${i}`}
          provenance={p}
          surface={surface}
          onClick={() => openDrawer(p, messageId ?? null)}
        />
      ))}
    </div>
  );
}
